/**
 * First-session analytics helper.
 *
 * Fires "first_*" milestone events exactly once per learner per browser using
 * localStorage flags. Designed for the Launch Path activation arc — tracking
 * the very first lesson/simulator/quiz a new user touches. These events feed
 * activation dashboards without duplicating the broader analytics taxonomy.
 *
 * Never include teen answer text, free-form responses, or PII.
 */
import { trackMetaEvent } from "@/lib/metaPixel";

const PREFIX = "hw_first_";

function once(key: string): boolean {
  if (typeof window === "undefined") return false;
  const k = PREFIX + key;
  try {
    if (localStorage.getItem(k)) return false;
    localStorage.setItem(k, "1");
    return true;
  } catch {
    return false;
  }
}

export function fireFirstLessonStarted(meta: { source: "launch_path" | "direct" }) {
  if (!once("lesson_started")) return;
  void trackMetaEvent("first_lesson_started", { customData: { source: meta.source } });
}

export function fireFirstLessonCompleted(meta: { source: "launch_path" | "direct" }) {
  if (!once("lesson_completed")) return;
  void trackMetaEvent("first_lesson_completed", { customData: { source: meta.source } });
}

export function fireFirstQuizCompleted() {
  if (!once("quiz_completed")) return;
  void trackMetaEvent("first_quiz_completed", {});
}

export function fireFirstSimulationStarted(slug: string) {
  if (!once("simulation_started")) return;
  void trackMetaEvent("first_simulation_started", { customData: { module_key: slug } });
}

export function fireFirstSimulationCompleted(slug: string) {
  if (!once("simulation_completed")) return;
  void trackMetaEvent("first_simulation_completed", { customData: { module_key: slug } });
}

export function fireFirstParentInsightGenerated(meta: { topic?: string } = {}) {
  if (!once("parent_insight_generated")) return;
  void trackMetaEvent("first_parent_insight_generated", {
    customData: meta.topic ? { topic: meta.topic } : {},
  });
}
